import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import './index.css';

import LoginPage from './pages/LoginPage';
import LaunchPage from './pages/LaunchPage';
import { LaunchAurora, LaunchBlueprint, LaunchMomentum, LaunchConstellation, LaunchExecutive } from './pages/LaunchDesigns';
import AppHubPage from './pages/AppHubPage';
import IppPage from './pages/IppPage';
import FinanceRequestsPage from './pages/FinanceRequestsPage';
import EdrmsPage from './pages/EdrmsPage';
import TechOpsPage from './pages/TechOpsPage';
import TechOpsHub from './pages/TechOpsHub';
import NocPage from './pages/NocPage';
import AttendancePage from './pages/AttendancePage';
import HrAssistantPage from './pages/HrAssistantPage';
import UserInformationPage from './pages/UserInformationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RegisterPage from './pages/RegisterPage';
import AppShell from './pages/AppShell';
import SpacePage from './pages/SpacePage';
import ListPage from './pages/ListPage';
import DashboardPage from './pages/DashboardPage';
import GanttPage from './pages/GanttPage';
import CalendarPage from './pages/CalendarPage';
import TimesheetPage from './pages/TimesheetPage';
import ResourcePage from './pages/ResourcePage';
import PaymentMilestonesPage from './pages/PaymentMilestonesPage';
import FinancialAnalysisPage from './pages/FinancialAnalysisPage';
import RiskRegisterPage from './pages/RiskRegisterPage';
import MajorMilestonesPage from './pages/MajorMilestonesPage';
import UsersPage from './pages/UsersPage';
import WBSPage from './pages/WBSPage';
import PhaseDashboard from './pages/PhaseDashboard';

// ── CRM ──────────────────────────────────────────────────────────────────────
import CrmShell from './pages/crm/CrmShell';
import CrmDashboard from './pages/crm/CrmDashboard';
import CrmPipeline from './pages/crm/CrmPipeline';
import CrmDealDetail from './pages/crm/CrmDealDetail';
import { CrmAccounts, CrmAccountDetail } from './pages/crm/CrmAccounts';
import CrmInventory from './pages/crm/CrmInventory';
import CrmActivities from './pages/crm/CrmActivities';
import CrmStock from './pages/crm/CrmStock';
import CrmSettings from './pages/crm/CrmSettings';
import CrmGoals from './pages/crm/CrmGoals';
import CrmForecast from './pages/crm/CrmForecast';
import CrmApprovals from './pages/crm/CrmApprovals';
import CrmCategories from './pages/crm/CrmCategories';
import { CrmQuotes, CrmSalesOrders, CrmInvoices } from './pages/crm/CrmDocumentsList';
import { QuoteEditor, SalesOrderEditor, InvoiceEditor } from './pages/crm/CrmDocumentEditor';
import { QuoteDetail, SalesOrderDetail, InvoiceDetail } from './pages/crm/CrmDocumentDetail';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'var(--muted)',gap:'18px',flexDirection:'column',background:'var(--bg)'}}>
      <div style={{width:48,height:48,borderRadius:'13px',background:'linear-gradient(135deg,#5B5BD6,#7C3AED)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',fontWeight:'900',color:'#fff',letterSpacing:'-0.5px',boxShadow:'0 6px 20px rgba(91,91,214,0.35)'}}>E</div>
      <div style={{textAlign:'center',lineHeight:1.4}}>
        <div style={{fontSize:'15px',fontWeight:'800',color:'var(--text)',letterSpacing:'-0.3px'}}>EOMP</div>
        <div style={{fontSize:'11px',color:'var(--muted)'}}>Enterprise Operations Management Platform</div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'8px',color:'var(--muted)',fontSize:'12.5px'}}>
        <div style={{width:14,height:14,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
        Loading your workspace…
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Guard for the CRM workspace — only users with a CRM role (or superadmin) may enter.
function RequireCrm({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--muted)', background: 'var(--bg)' }}>Loading…</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  const crmRole = user.crm_role || 'none';
  if (crmRole === 'none' && user.role !== 'superadmin') return <Navigate to="/spaces" replace />;
  return children;
}

export default function App() {
  // H1 fix: auto-logout after 30 minutes of inactivity
  const IDLE_MS = 30 * 60 * 1000; // 30 minutes
  React.useEffect(() => {
    let timer = null;
    function reset() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Clear auth and redirect to login
        localStorage.removeItem('flowup_token');
        document.cookie = 'trajectory_token=; Max-Age=0; path=/';
        window.location.href = '/login';
      }, IDLE_MS);
    }
    const events = ['mousemove','mousedown','keydown','touchstart','scroll','click'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset(); // start timer
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/launch" element={<LaunchPage />} />
          <Route path="/launch/1" element={<LaunchAurora />} />
          <Route path="/launch/2" element={<LaunchBlueprint />} />
          <Route path="/launch/3" element={<LaunchMomentum />} />
          <Route path="/launch/4" element={<LaunchConstellation />} />
          <Route path="/launch/5" element={<LaunchExecutive />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* ── App Hub launcher (own top bar, sits above the app shells) ── */}
          <Route path="/hub" element={<RequireAuth><AppHubPage /></RequireAuth>} />

          {/* ── Individual Performance Plan (own top bar) ── */}
          <Route path="/ipp" element={<RequireAuth><IppPage /></RequireAuth>} />

          {/* ── Finance Requests (own top bar) ── */}
          <Route path="/finance-requests" element={<RequireAuth><FinanceRequestsPage /></RequireAuth>} />
          <Route path="/edrms" element={<RequireAuth><EdrmsPage /></RequireAuth>} />
          <Route path="/techops" element={<RequireAuth><TechOpsHub /></RequireAuth>} />
          <Route path="/techops/change" element={<RequireAuth><TechOpsPage /></RequireAuth>} />
          <Route path="/techops/noc" element={<RequireAuth><NocPage /></RequireAuth>} />
          <Route path="/attendance" element={<RequireAuth><AttendancePage /></RequireAuth>} />
          <Route path="/hr-assistant" element={<RequireAuth><HrAssistantPage /></RequireAuth>} />

          {/* ── Shared User Information (own top bar) ── */}
          <Route path="/profile" element={<RequireAuth><UserInformationPage /></RequireAuth>} />

          <Route path="/" element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route index element={<Navigate to="/hub" replace />} />
            <Route path="spaces" element={<ErrorBoundary><SpacePage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/gantt" element={<ErrorBoundary><GanttPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/timesheet" element={<ErrorBoundary><TimesheetPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/resources" element={<ErrorBoundary><ResourcePage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/payments" element={<ErrorBoundary><PaymentMilestonesPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/financial" element={<ErrorBoundary><FinancialAnalysisPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/risks" element={<ErrorBoundary><RiskRegisterPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/milestones" element={<ErrorBoundary><MajorMilestonesPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/wbs" element={<ErrorBoundary><WBSPage /></ErrorBoundary>} />
            <Route path="spaces/:spaceId/calendar" element={<ErrorBoundary><CalendarPage /></ErrorBoundary>} />
            <Route path="phases/:phaseId/dashboard" element={<ErrorBoundary><PhaseDashboard /></ErrorBoundary>} />
            <Route path="lists/:listId" element={<ErrorBoundary><ListPage /></ErrorBoundary>} />
            <Route path="users" element={<ErrorBoundary><UsersPage /></ErrorBoundary>} />
          </Route>

          {/* ── CRM workspace (separate shell, flip from the project app) ── */}
          <Route path="/crm" element={<RequireCrm><CrmShell /></RequireCrm>}>
            <Route index element={<ErrorBoundary><CrmDashboard /></ErrorBoundary>} />
            <Route path="pipeline" element={<ErrorBoundary><CrmPipeline /></ErrorBoundary>} />
            <Route path="deals/:id" element={<ErrorBoundary><CrmDealDetail /></ErrorBoundary>} />
            <Route path="accounts" element={<ErrorBoundary><CrmAccounts /></ErrorBoundary>} />
            <Route path="accounts/:id" element={<ErrorBoundary><CrmAccountDetail /></ErrorBoundary>} />
            <Route path="activities" element={<ErrorBoundary><CrmActivities /></ErrorBoundary>} />
            <Route path="inventory" element={<ErrorBoundary><CrmInventory /></ErrorBoundary>} />
            <Route path="stock" element={<ErrorBoundary><CrmStock /></ErrorBoundary>} />
            <Route path="settings" element={<ErrorBoundary><CrmSettings /></ErrorBoundary>} />
            <Route path="goals" element={<ErrorBoundary><CrmGoals /></ErrorBoundary>} />
            <Route path="forecast" element={<ErrorBoundary><CrmForecast /></ErrorBoundary>} />
            <Route path="approvals" element={<ErrorBoundary><CrmApprovals /></ErrorBoundary>} />
            <Route path="categories" element={<ErrorBoundary><CrmCategories /></ErrorBoundary>} />
            <Route path="quotes" element={<ErrorBoundary><CrmQuotes /></ErrorBoundary>} />
            <Route path="quotes/new" element={<ErrorBoundary><QuoteEditor /></ErrorBoundary>} />
            <Route path="quotes/:id" element={<ErrorBoundary><QuoteDetail /></ErrorBoundary>} />
            <Route path="quotes/:id/edit" element={<ErrorBoundary><QuoteEditor /></ErrorBoundary>} />
            <Route path="sales-orders" element={<ErrorBoundary><CrmSalesOrders /></ErrorBoundary>} />
            <Route path="sales-orders/new" element={<ErrorBoundary><SalesOrderEditor /></ErrorBoundary>} />
            <Route path="sales-orders/:id" element={<ErrorBoundary><SalesOrderDetail /></ErrorBoundary>} />
            <Route path="sales-orders/:id/edit" element={<ErrorBoundary><SalesOrderEditor /></ErrorBoundary>} />
            <Route path="invoices" element={<ErrorBoundary><CrmInvoices /></ErrorBoundary>} />
            <Route path="invoices/new" element={<ErrorBoundary><InvoiceEditor /></ErrorBoundary>} />
            <Route path="invoices/:id" element={<ErrorBoundary><InvoiceDetail /></ErrorBoundary>} />
            <Route path="invoices/:id/edit" element={<ErrorBoundary><InvoiceEditor /></ErrorBoundary>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
